import { AliasToken } from 'antd/es/theme/interface'
import darkColors from '../../dark-colors.module.scss'
import lightColors from '../../light-colors.module.scss'
import { ComponentsToken } from 'antd/lib/theme/context'
import { convertPxToNumber } from '../../../utils/helpers.ts'

// Per-language fonts live in index.css under html.lang-*. Keeping a language
// value here would make every language change invalidate the antd theme and
// re-render the whole tree; 'inherit' is constant and still picks up that font.
// Omitting fontFamily is not equivalent — antd would fall back to its own
// default stack instead of inheriting.
const FONT_FAMILY = 'inherit'

export const getDarkToken = (): Partial<AliasToken> => {
  return {
    fontFamily: FONT_FAMILY,

    colorPrimary: darkColors.blue6,
    colorPrimaryBg: '#1C5B7D',
    colorPrimaryBgHover: darkColors.blue7,
    colorPrimaryBorder: darkColors.blue8,
    colorPrimaryBorderHover: darkColors.blue7,
    colorPrimaryHover: darkColors.blue9,
    colorPrimaryActive: darkColors.blue5,
    colorPrimaryTextHover: darkColors.blue10,
    colorPrimaryText: darkColors.blue10,
    colorPrimaryTextActive: darkColors.blue5,

    blue1: darkColors.blue1,
    blue2: darkColors.blue2,
    blue3: darkColors.blue3,
    blue4: darkColors.blue4,
    blue5: darkColors.blue5,
    blue6: darkColors.blue6,
    blue7: darkColors.blue7,
    blue8: darkColors.blue8,
    blue9: darkColors.blue9,
    blue10: darkColors.blue10,

    yellow1: darkColors.yellow1,
    yellow2: darkColors.yellow2,
    yellow3: darkColors.yellow3,
    yellow4: darkColors.yellow4,
    yellow5: darkColors.yellow5,
    yellow6: darkColors.yellow6,
    yellow7: darkColors.yellow7,
    yellow8: darkColors.yellow8,
    yellow9: darkColors.yellow9,
    yellow10: darkColors.yellow10,

    paddingMD: 16,
  }
}

export const getDarkComponents = (): Partial<ComponentsToken> => {
  return {
    Button: {
      defaultBg: darkColors.blue6,
      defaultHoverBg: darkColors.btnBgHover,
      defaultActiveBg: darkColors.blue5,
      colorBgContainerDisabled: darkColors.btnBgDisable,
      colorBorder: darkColors.blue8,
      colorText: darkColors.btnTextText,
      colorError: darkColors.btnDangerBg,
      colorErrorBgHover: darkColors.btnDangerBgHover,
      colorErrorText: '#FFFFFF',
      primaryColor: '#FFFFFF',
      borderColorDisabled: darkColors.colorBorder,
      defaultGhostBorderColor: darkColors.blue8,
      defaultGhostColor: darkColors.blue10,
    },
    Checkbox: {
      colorPrimary: darkColors.blue6,
      colorText: darkColors.checkBoxText,
    },
    Switch: {
      colorPrimary: darkColors.switchBg,
      colorPrimaryHover: darkColors.switchBg,
    },
    Menu: {
      itemBg: darkColors.naviBgPrimary,
      subMenuItemBg: darkColors.naviBgSecondary,
      itemSelectedBg: darkColors.naviBtnFocused,
      itemSelectedColor: darkColors.naviText,
      colorText: darkColors.naviText,
      popupBg: darkColors.blue7,
      subMenuItemSelectedColor: darkColors.naviText,
    },
    Layout: {
      headerBg: darkColors.naviBgPrimary,
      siderBg: darkColors.naviBgPrimary,
      footerBg: darkColors.footerBg,
    },
    Segmented: {
      trackBg: darkColors.colorBorder,
    },
    Form: {
      itemMarginBottom: convertPxToNumber(darkColors.marginBase),
    },
    Radio: {
      colorPrimary: darkColors.blue8,
      colorBgContainer: '#1C1C1C',
    },
    Tabs: {
      itemSelectedColor: darkColors.blue8,
      inkBarColor: darkColors.blue8,
    },
    Drawer: {
      colorBgElevated: darkColors.naviBgPrimary,
    },
  }
}

export const getLightComponents = (): Partial<ComponentsToken> => {
  return {
    Button: {
      defaultBg: lightColors.blue6,
      defaultHoverBg: lightColors.btnBgHover,
      defaultActiveBg: lightColors.blue5,
      colorBgContainerDisabled: lightColors.btnBgDisable,
      colorBorder: lightColors.blue8,
      //colorText: lightColors.btnTextText,
      colorText: lightColors.blue9,
      colorError: lightColors.btnDangerBg,
      colorErrorBgHover: lightColors.btnDangerBgHover,
      colorErrorText: '#FFFFFF',
      borderColorDisabled: lightColors.colorBorder,
      defaultGhostBorderColor: lightColors.colorPrimaryBorder,
      defaultGhostColor: lightColors.blue9,
    },
    Checkbox: {
      colorPrimary: lightColors.blue9,
      colorText: lightColors.checkBoxText,
    },
    Switch: {
      colorPrimary: lightColors.switchBg,
      colorPrimaryHover: lightColors.switchBg,
    },
    Menu: {
      itemBg: lightColors.naviBgPrimary,
      subMenuItemBg: lightColors.naviBgSecondary,
      itemHoverBg: lightColors.blue4,
      itemSelectedBg: lightColors.naviBtnFocused,
      itemSelectedColor: lightColors.naviText,
      colorText: lightColors.naviText,
      subMenuItemSelectedColor: lightColors.naviText,
    },
    Layout: {
      headerBg: lightColors.naviBgPrimary,
      siderBg: lightColors.naviBgPrimary,
      footerBg: lightColors.footerBg,
    },
    Segmented: {
      trackBg: 'rgba(217, 217, 217, 1)',
    },
    Form: {
      itemMarginBottom: convertPxToNumber(lightColors.marginBase),
    },
    Radio: {
      colorPrimary: lightColors.blue8,
      colorBgContainer: '#FFFFFF',
    },
    Tabs: {
      itemSelectedColor: lightColors.blue8,
      inkBarColor: lightColors.blue8,
    },
    Drawer: {
      colorBgElevated: lightColors.naviBgPrimary,
    },
  }
}

export const getLightToken = (): Partial<AliasToken> => {
  return {
    fontFamily: FONT_FAMILY,

    colorPrimary: lightColors.blue6,
    colorPrimaryBg: '#CEE6F0',
    colorPrimaryBgHover: lightColors.blue4,
    colorPrimaryBorder: lightColors.blue8,
    colorPrimaryBorderHover: lightColors.blue7,
    colorPrimaryHover: lightColors.blue9,
    colorPrimaryActive: lightColors.blue7,
    colorPrimaryTextHover: lightColors.blue8,
    colorPrimaryText: lightColors.blue9,
    colorPrimaryTextActive: lightColors.blue7,

    blue1: lightColors.blue1,
    blue2: lightColors.blue2,
    blue3: lightColors.blue3,
    blue4: lightColors.blue4,
    blue5: lightColors.blue5,
    blue6: lightColors.blue6,
    blue7: lightColors.blue7,
    blue8: lightColors.blue8,
    blue9: lightColors.blue9,
    blue10: lightColors.blue10,

    yellow1: lightColors.yellow1,
    yellow2: lightColors.yellow2,
    yellow3: lightColors.yellow3,
    yellow4: lightColors.yellow4,
    yellow5: lightColors.yellow5,
    yellow6: lightColors.yellow6,
    yellow7: lightColors.yellow7,
    yellow8: lightColors.yellow8,
    yellow9: lightColors.yellow9,
    yellow10: lightColors.yellow10,

    paddingMD: 16,
  }
}
